import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UndoIcon from '@mui/icons-material/Undo';
import { ReturnDialog } from '../../components/Evaluation/ReturnDialog';
import { ReturnCommentHistory } from '../../components/Evaluation/ReturnCommentHistory';
import {
  GoalReferencePanel,
  type EvaluationGoalReference,
} from '../../components/Evaluation/GoalReferencePanel';
import {
  ItemGoalReference,
  hasAnyItemGoal,
} from '../../components/Evaluation/ItemGoalReference';
import {
  canReject,
  type EvaluationStatus
} from '../../utils/statusLabels';
import { getScoreDescription, getShortScoreDescription, getScoreColor } from '../../utils/scoreDescription';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';

interface EvaluationItem {
  item_detail_id: string;
  item_id: string;
  category_number: number;
  category_name: string;
  item_number: string;
  item_description: string;
  weight: number;
  target_description?: string;
  achievement_criteria?: string;
  first_evaluation_score?: number;
  first_evaluation_comment?: string;
  second_evaluation_score?: number;
  second_evaluation_comment?: string;
  third_evaluation_score?: number;
  third_evaluation_comment?: string;
}

interface QuantitativeData {
  annual_sales?: number;
  current_rank_cost?: number;
  domestic_member_count?: number;
  offshore_member_count?: number;
  team_member_count?: number;
  quantitative_dp_value?: number;
  quantitative_dp_score?: number;
  // 営業部門用
  target_expected_order?: number;
  target_sales?: number;
  target_gross_profit?: number;
  actual_expected_order?: number;
  actual_sales?: number;
  actual_gross_profit?: number;
}

interface PerformanceResult {
  performance_score: number | null;
  salary_increase_rate: number | null;
}

interface EvaluatorItemScore {
  evaluator_id: string;
  evaluation_category: string;
  item_id: string;
  evaluation_score: number;
  evaluation_comment?: string;
  evaluator_name: string;
  evaluation_weight: number;
}

const DeptHeadEvaluationFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { employeeId, periodId } = useParams<{ employeeId: string; periodId: string }>();
  const { user, periodStatus } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const [evaluation, setEvaluation] = useState<any>(null);
  const [departmentType, setDepartmentType] = useState<string>('');
  const [qualitativeItems, setQualitativeItems] = useState<EvaluationItem[]>([]);
  const [quantitativeData, setQuantitativeData] = useState<QuantitativeData>({});
  const [performanceResult, setPerformanceResult] = useState<PerformanceResult | null>(null);
  const [evaluatorItemScores, setEvaluatorItemScores] = useState<EvaluatorItemScore[]>([]);
  const [goal, setGoal] = useState<EvaluationGoalReference | null>(null);

  const [formData, setFormData] = useState<{
    [key: string]: {
      third_evaluation_score?: number;
      third_evaluation_comment?: string;
    };
  }>({});

  // Return dialog state
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);

  useEffect(() => {
    fetchEvaluationData();
  }, [employeeId, periodId]);

  const fetchEvaluationData = async () => {
    try {
      setLoading(true);
      setError('');

      // AuthContextから現在のユーザーIDを取得（localStorageではなくuser?.employee_id）
      const currentUserId = user?.employee_id;

      const response = await api.get(
        `/dept-head-evaluation/${employeeId}/${periodId}`
      );

      if (response.data.success) {
        const data = response.data.data;
        setEvaluation(data.evaluation);
        setDepartmentType(data.department_type);
        setPerformanceResult(data.performance_result);
        setGoal(data.goal ?? null);

        if (data.qualitative_items) {
          setQualitativeItems(data.qualitative_items);

          // 評価者別の項目スコアを設定
          if (data.evaluatorItemScores) {
            setEvaluatorItemScores(data.evaluatorItemScores);
          }

          // Initialize form data from evaluatorItemScores if exists (for current user), otherwise use defaults
          const initialFormData: any = {};
          data.qualitative_items.forEach((item: EvaluationItem) => {
            // Find this evaluator's saved score for this item
            const savedScore = data.evaluatorItemScores?.find(
              (eis: any) => eis.evaluator_id === currentUserId && eis.item_id === item.item_id
            );

            initialFormData[item.item_detail_id] = {
              third_evaluation_score: savedScore?.evaluation_score || item.third_evaluation_score || 6,
              third_evaluation_comment: savedScore?.evaluation_comment || item.third_evaluation_comment || '',
            };
          });
          setFormData(initialFormData);
        }

        if (data.quantitative_data) {
          setQuantitativeData(data.quantitative_data);
        }

        // Check if this evaluator has already submitted using isCompleted flag from backend
        if (data.isCompleted === true) {
          setSubmitted(true);
        }
      }
    } catch (err) {
      console.error('評価データ取得エラー:', err);
      setError(extractErrorMessage(err, '評価データの取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (itemDetailId: string, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [itemDetailId]: {
        ...prev[itemDetailId],
        [field]: value,
      },
    }));
  };

  const handleQuantitativeChange = (field: string, value: any) => {
    // メンバー数は整数のみ受け付ける
    if (field === 'domestic_member_count' || field === 'offshore_member_count') {
      if (value === '' || value === null || value === undefined) {
        setQuantitativeData((prev) => ({
          ...prev,
          [field]: null,
        }));
      } else {
        const intValue = parseInt(value);
        if (!isNaN(intValue) && intValue >= 0) {
          setQuantitativeData((prev) => ({
            ...prev,
            [field]: intValue,
          }));
        }
      }
    } else {
      setQuantitativeData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload: any = {
        evaluationId: evaluation.evaluation_id,
      };

      // 部門種別ごとに送信データを構築
      if (departmentType === 'tech') {
        // 技術部門：定性 + 定量
        payload.qualitative_items = qualitativeItems.map((item) => ({
          item_id: item.item_id,
          evaluation_score: formData[item.item_detail_id]?.third_evaluation_score,
          evaluation_comment: formData[item.item_detail_id]?.third_evaluation_comment,
        }));
        payload.quantitative_data = {
          annual_sales: Number(quantitativeData.annual_sales) || 0,
          domestic_member_count: Number(quantitativeData.domestic_member_count) || 0,
          offshore_member_count: Number(quantitativeData.offshore_member_count) || 0,
        };
      } else if (departmentType === 'sales') {
        // 営業部門：定量のみ
        payload.quantitative_data = {
          target_expected_order: Number(quantitativeData.target_expected_order) || 0,
          target_sales: Number(quantitativeData.target_sales) || 0,
          target_gross_profit: Number(quantitativeData.target_gross_profit) || 0,
          actual_expected_order: Number(quantitativeData.actual_expected_order) || 0,
          actual_sales: Number(quantitativeData.actual_sales) || 0,
          actual_gross_profit: Number(quantitativeData.actual_gross_profit) || 0,
        };
      } else if (departmentType === 'admin') {
        // 管理部門：定性のみ
        payload.qualitative_items = qualitativeItems.map((item) => ({
          item_id: item.item_id,
          evaluation_score: formData[item.item_detail_id]?.third_evaluation_score,
          evaluation_comment: formData[item.item_detail_id]?.third_evaluation_comment,
        }));
      }

      const response = await api.put(
        '/dept-head-evaluation/draft',
        payload
      );

      if (response.data.success) {
        setSuccess('部門長評価を一時保存しました');
        // 評価結果を更新
        if (response.data.performance_result) {
          setPerformanceResult(response.data.performance_result);
        }
        // 一覧画面に戻る
        setTimeout(() => {
          navigate(`/dept-head-evaluation/${periodId}`);
        }, 1500);
      }
    } catch (err) {
      console.error('一時保存エラー:', err);
      setError(extractErrorMessage(err, '一時保存に失敗しました'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload: any = {
        evaluationId: evaluation.evaluation_id,
      };

      // 部門種別ごとに送信データを構築
      if (departmentType === 'tech') {
        payload.qualitative_items = qualitativeItems.map((item) => ({
          item_detail_id: item.item_detail_id,
          item_number: item.item_number,
          item_description: item.item_description,
          second_evaluation_score: item.second_evaluation_score,
          third_evaluation_score: formData[item.item_detail_id]?.third_evaluation_score,
          third_evaluation_comment: formData[item.item_detail_id]?.third_evaluation_comment,
          weight: item.weight,
        }));
        payload.quantitative_data = {
          annual_sales: Number(quantitativeData.annual_sales) || 0,
          domestic_member_count: Number(quantitativeData.domestic_member_count) || 0,
          offshore_member_count: Number(quantitativeData.offshore_member_count) || 0,
        };
      } else if (departmentType === 'sales') {
        payload.quantitative_data = {
          target_expected_order: Number(quantitativeData.target_expected_order) || 0,
          target_sales: Number(quantitativeData.target_sales) || 0,
          target_gross_profit: Number(quantitativeData.target_gross_profit) || 0,
          actual_expected_order: Number(quantitativeData.actual_expected_order) || 0,
          actual_sales: Number(quantitativeData.actual_sales) || 0,
          actual_gross_profit: Number(quantitativeData.actual_gross_profit) || 0,
        };
      } else if (departmentType === 'admin') {
        payload.qualitative_items = qualitativeItems.map((item) => ({
          item_detail_id: item.item_detail_id,
          item_number: item.item_number,
          item_description: item.item_description,
          second_evaluation_score: item.second_evaluation_score,
          third_evaluation_score: formData[item.item_detail_id]?.third_evaluation_score,
          third_evaluation_comment: formData[item.item_detail_id]?.third_evaluation_comment,
          weight: item.weight,
        }));
      }

      const response = await api.put(
        '/dept-head-evaluation/submit',
        payload
      );

      if (response.data.success) {
        setSuccess('部門長評価を提出しました');
        // 評価結果を更新
        if (response.data.performance_result) {
          setPerformanceResult(response.data.performance_result);
        }
        setTimeout(() => {
          navigate(`/dept-head-evaluation/${periodId}`);
        }, 2000);
      }
    } catch (err) {
      console.error('提出エラー:', err);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errAny = err as any;
      if (Array.isArray(errAny.response?.data?.errors)) {
        // スコア差の業務エラー（item_number付き）と、フィールド級エラー（field付き）の双方に対応
        const errorMessages = errAny.response.data.errors
          .map((e: { item_number?: string; field?: string; message?: string; msg?: string }) =>
            e.item_number ? `${e.item_number}: ${e.message ?? e.msg ?? ''}` : (e.message ?? e.msg ?? '')
          )
          .filter((m: string) => m)
          .join('\n');
        setError(`確認が必要な項目があります：\n${errorMessages}`);
      } else {
        setError(extractErrorMessage(err, '提出に失敗しました'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReturn = async (comment: string) => {
    if (!evaluation?.evaluation_id) return;

    try {
      const response = await api.post(
        `/evaluations/${evaluation.evaluation_id}/reject`,
        { comment }
      );

      if (response.data.success) {
        setSuccess('評価を差し戻しました。上長に修正を依頼しました。');
        setReturnDialogOpen(false);
        // 差し戻し成功後は評価一覧画面にリダイレクト
        setTimeout(() => {
          navigate(`/dept-head-evaluation/${periodId}`);
        }, 1500);
      }
    } catch (error) {
      console.error('差し戻しエラー:', error);
      setError(extractErrorMessage(error, '差し戻しに失敗しました'));
    }
  };

  const canReturnEvaluation = () => {
    return evaluation?.status
      ? canReject(evaluation.status as EvaluationStatus, 'dept_head', {
          managerEvalDisabled: evaluation.manager_eval_disabled === true,
        })
      : false;
  };

  const canEvaluate =
    !submitted &&
    ((periodStatus?.canSubmitDeptEvaluation ?? true) || evaluation?.allow_out_of_period_edit === true) &&
    (evaluation?.status === 'manager_evaluated' ||
      evaluation?.status === 'self_evaluated');

  const getDifferenceColor = (diff: number) => {
    if (diff <= 2) return 'success';
    if (diff === 3) return 'warning';
    return 'error';
  };

  // 上長評価者の一覧を取得（重複除外）
  const getManagerEvaluators = () => {
    const managers = evaluatorItemScores.filter(
      (score) => score.evaluation_category === 'manager'
    );
    // evaluator_idでユニークにする
    const uniqueEvaluators = Array.from(
      new Map(managers.map((m) => [m.evaluator_id, m])).values()
    );
    return uniqueEvaluators;
  };

  // 特定の上長評価者の特定項目の評価点を取得
  const getEvaluatorScore = (evaluatorId: string, itemId: string): number | null => {
    const score = evaluatorItemScores.find(
      (s) => s.evaluator_id === evaluatorId && s.item_id === itemId
    );
    return score?.evaluation_score ?? null;
  };

  // 特定の上長評価者の特定項目のコメントを取得
  const getEvaluatorComment = (evaluatorId: string, itemId: string): string | null => {
    const score = evaluatorItemScores.find(
      (s) => s.evaluator_id === evaluatorId && s.item_id === itemId
    );
    return score?.evaluation_comment ?? null;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>読み込み中...</Typography>
      </Container>
    );
  }

  if (!evaluation) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">評価データが見つかりません</Alert>
      </Container>
    );
  }

  // カテゴリごとにグループ化
  const groupedItems = qualitativeItems.reduce((acc, item) => {
    if (!acc[item.category_number]) {
      acc[item.category_number] = {
        category_name: item.category_name,
        items: [],
      };
    }
    acc[item.category_number].items.push(item);
    return acc;
  }, {} as { [key: number]: { category_name: string; items: EvaluationItem[] } });

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            部門長評価
            {departmentType === 'tech' && '（技術部門）'}
            {departmentType === 'sales' && '（営業部門）'}
            {departmentType === 'admin' && '（管理部門）'}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                評価対象者: {evaluation.employee_name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                部署: {evaluation.department_name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                職級: {evaluation.job_grade}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                役職: {evaluation.job_title}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <GoalReferencePanel
          goal={goal}
          title="目標の申請状況"
          itemGoalsShown={hasAnyItemGoal(qualitativeItems)}
        />

        {/* 技術部門：定性評価 + 定量評価 */}
        {departmentType === 'tech' && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              【定性評価】
            </Typography>
            {Object.keys(groupedItems)
              .sort()
              .map((categoryNumber) => {
                const category = groupedItems[Number(categoryNumber)];
                return (
                  <Box key={categoryNumber} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {category.category_name}
                    </Typography>
                    {category.items.map((item) => {
                      const diff = item.second_evaluation_score && formData[item.item_detail_id]?.third_evaluation_score
                        ? Math.abs(item.second_evaluation_score - formData[item.item_detail_id]!.third_evaluation_score!)
                        : 0;

                      return (
                        <Accordion key={item.item_detail_id} defaultExpanded>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>
                              {item.item_number}: {item.item_description}（重み: {item.weight}）
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <ItemGoalReference
                                  targetDescription={item.target_description}
                                  achievementCriteria={item.achievement_criteria}
                                  title="この項目の目標（確定）"
                                />
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  自己評価
                                </Typography>
                                <Typography variant="body1">評価点: {item.first_evaluation_score || '－'}点</Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  {item.first_evaluation_comment || '（コメントなし）'}
                                </Typography>
                              </Grid>

                              <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2" color="text.secondary" gutterBottom fontWeight="bold">
                                  上長評価
                                </Typography>
                                {getManagerEvaluators().length > 0 ? (
                                  getManagerEvaluators().map((evaluator) => {
                                    const evalScore = getEvaluatorScore(evaluator.evaluator_id, item.item_id);
                                    const evalComment = getEvaluatorComment(evaluator.evaluator_id, item.item_id);
                                    return (
                                      <Box
                                        key={evaluator.evaluator_id}
                                        sx={{
                                          mb: 2,
                                          p: 2,
                                          bgcolor: 'grey.50',
                                          borderRadius: 1,
                                          borderLeft: 3,
                                          borderColor: 'primary.main',
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                          <Typography variant="body2" fontWeight="bold" color="primary">
                                            {evaluator.evaluator_name}
                                          </Typography>
                                          <Chip
                                            label={`重み: ${(evaluator.evaluation_weight * 100).toFixed(0)}%`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                          />
                                        </Box>
                                        <Typography variant="body1" sx={{ mb: 0.5 }}>
                                          評価点: {evalScore !== null ? `${evalScore}点` : '－'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {evalComment || '（コメントなし）'}
                                        </Typography>
                                      </Box>
                                    );
                                  })
                                ) : (
                                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="body1">評価点: {item.second_evaluation_score || '－'}点</Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                      {item.second_evaluation_comment || '（コメントなし）'}
                                    </Typography>
                                  </Box>
                                )}
                              </Grid>

                              <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                  <Typography variant="subtitle2">部門長評価点（1-10点）</Typography>
                                  <Box display="flex" gap={1} alignItems="center">
                                    {item.second_evaluation_score && (
                                      <Chip
                                        label={`上長評価との差: ${diff}点`}
                                        color={getDifferenceColor(diff)}
                                        size="small"
                                      />
                                    )}
                                    <Chip
                                      label={`${formData[item.item_detail_id]?.third_evaluation_score || 6}点 - ${getShortScoreDescription(formData[item.item_detail_id]?.third_evaluation_score || 6)}`}
                                      color={getScoreColor(formData[item.item_detail_id]?.third_evaluation_score || 6)}
                                      size="small"
                                    />
                                  </Box>
                                </Box>
                                <Slider
                                  value={formData[item.item_detail_id]?.third_evaluation_score || 6}
                                  onChange={(_, value) =>
                                    handleInputChange(item.item_detail_id, 'third_evaluation_score', value as number)
                                  }
                                  min={1}
                                  max={10}
                                  step={1}
                                  marks={[
                                    { value: 1, label: '1' },
                                    { value: 6, label: '6 (基準)' },
                                    { value: 10, label: '10' },
                                  ]}
                                  valueLabelDisplay="auto"
                                />
                                {/* 評価点の詳細説明 */}
                                <Box
                                  sx={{
                                    mt: 1,
                                    p: 2,
                                    bgcolor: getScoreDescription(formData[item.item_detail_id]?.third_evaluation_score || 6).isStandard
                                      ? 'info.50'
                                      : 'grey.50',
                                    borderRadius: 1,
                                    borderLeft: 4,
                                    borderColor: getScoreDescription(formData[item.item_detail_id]?.third_evaluation_score || 6).isStandard
                                      ? 'info.main'
                                      : 'grey.300',
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary" display="block" fontWeight="bold" mb={0.5}>
                                    評価点の意味：
                                  </Typography>
                                  <Typography variant="body2" color="text.primary">
                                    {getScoreDescription(formData[item.item_detail_id]?.third_evaluation_score || 6).description}
                                  </Typography>
                                </Box>
                              </Grid>

                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={4}
                                  label="部門長評価コメント"
                                  value={formData[item.item_detail_id]?.third_evaluation_comment || ''}
                                  onChange={(e) =>
                                    handleInputChange(item.item_detail_id, 'third_evaluation_comment', e.target.value)
                                  }
                                  placeholder="評価のコメントを入力してください&#13;&#10;例：&#13;&#10;・目標達成状況について&#13;&#10;・特に評価できる点&#13;&#10;・今後の期待や改善点"
                                  helperText="自己評価・上長評価を踏まえた部門長としてのコメントを記入してください"
                                />
                              </Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                );
              })}

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              【定量評価】
            </Typography>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="年間売上（円）"
                      value={quantitativeData.annual_sales || ''}
                      onChange={(e) => handleQuantitativeChange('annual_sales', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="国内メンバー数"
                      value={quantitativeData.domestic_member_count ?? ''}
                      onChange={(e) => handleQuantitativeChange('domestic_member_count', e.target.value)}
                      helperText="国内/BPメンバー数（0以上の整数）"
                      inputProps={{ min: 0, step: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="オフショアメンバー数"
                      value={quantitativeData.offshore_member_count ?? ''}
                      onChange={(e) => handleQuantitativeChange('offshore_member_count', e.target.value)}
                      helperText="オフショアメンバー数（0以上の整数、換算: 0.25）"
                      inputProps={{ min: 0, step: 1 }}
                    />
                  </Grid>
                  {quantitativeData.quantitative_dp_value !== undefined && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 4 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Dp計算値
                          </Typography>
                          <Typography variant="h6">
                            {quantitativeData.quantitative_dp_value !== null && quantitativeData.quantitative_dp_value !== undefined
                              ? Number(quantitativeData.quantitative_dp_value).toFixed(4)
                              : '-'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Dp評価点
                          </Typography>
                          <Typography variant="h6">
                            {quantitativeData.quantitative_dp_score !== null && quantitativeData.quantitative_dp_score !== undefined
                              ? Number(quantitativeData.quantitative_dp_score).toFixed(2)
                              : '-'}点
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </>
        )}

        {/* 営業部門：定量評価のみ */}
        {departmentType === 'sales' && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              【定量評価】営業指標
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              ※定性評価はありません。営業指標のみ入力してください。
            </Alert>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  目標値
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="目標期待受注額（円）"
                      value={quantitativeData.target_expected_order || ''}
                      onChange={(e) => handleQuantitativeChange('target_expected_order', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="目標売上高（円）"
                      value={quantitativeData.target_sales || ''}
                      onChange={(e) => handleQuantitativeChange('target_sales', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="目標受注粗利額（円）"
                      value={quantitativeData.target_gross_profit || ''}
                      onChange={(e) => handleQuantitativeChange('target_gross_profit', e.target.value)}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  実績値
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="実績期待受注額（円）"
                      value={quantitativeData.actual_expected_order || ''}
                      onChange={(e) => handleQuantitativeChange('actual_expected_order', e.target.value)}
                    />
                    {quantitativeData.target_expected_order && quantitativeData.actual_expected_order && (
                      <Typography variant="caption" color="text.secondary">
                        達成率:{' '}
                        {Number((Number(quantitativeData.actual_expected_order) / Number(quantitativeData.target_expected_order)) * 100).toFixed(1)}%
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="実績売上高（円）"
                      value={quantitativeData.actual_sales || ''}
                      onChange={(e) => handleQuantitativeChange('actual_sales', e.target.value)}
                    />
                    {quantitativeData.target_sales && quantitativeData.actual_sales && (
                      <Typography variant="caption" color="text.secondary">
                        達成率: {Number((Number(quantitativeData.actual_sales) / Number(quantitativeData.target_sales)) * 100).toFixed(1)}%
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="実績受注粗利額（円）"
                      value={quantitativeData.actual_gross_profit || ''}
                      onChange={(e) => handleQuantitativeChange('actual_gross_profit', e.target.value)}
                    />
                    {quantitativeData.target_gross_profit && quantitativeData.actual_gross_profit && (
                      <Typography variant="caption" color="text.secondary">
                        達成率:{' '}
                        {Number((Number(quantitativeData.actual_gross_profit) / Number(quantitativeData.target_gross_profit)) * 100).toFixed(1)}%
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </>
        )}

        {/* 管理部門：定性評価のみ */}
        {departmentType === 'admin' && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              【定性評価】
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              ※定量評価はありません。定性評価のみ入力してください。
            </Alert>
            {Object.keys(groupedItems)
              .sort()
              .map((categoryNumber) => {
                const category = groupedItems[Number(categoryNumber)];
                return (
                  <Box key={categoryNumber} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {category.category_name}
                    </Typography>
                    {category.items.map((item) => {
                      const diff = item.second_evaluation_score && formData[item.item_detail_id]?.third_evaluation_score
                        ? Math.abs(item.second_evaluation_score - formData[item.item_detail_id]!.third_evaluation_score!)
                        : 0;

                      return (
                        <Accordion key={item.item_detail_id} defaultExpanded>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>
                              {item.item_number}: {item.item_description}（重み: {item.weight}）
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <ItemGoalReference
                                  targetDescription={item.target_description}
                                  achievementCriteria={item.achievement_criteria}
                                  title="この項目の目標（確定）"
                                />
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  自己評価
                                </Typography>
                                <Typography variant="body1">評価点: {item.first_evaluation_score || '－'}点</Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  {item.first_evaluation_comment || '（コメントなし）'}
                                </Typography>
                              </Grid>

                              <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2" color="text.secondary" gutterBottom fontWeight="bold">
                                  上長評価
                                </Typography>
                                {getManagerEvaluators().length > 0 ? (
                                  getManagerEvaluators().map((evaluator) => {
                                    const evalScore = getEvaluatorScore(evaluator.evaluator_id, item.item_id);
                                    const evalComment = getEvaluatorComment(evaluator.evaluator_id, item.item_id);
                                    return (
                                      <Box
                                        key={evaluator.evaluator_id}
                                        sx={{
                                          mb: 2,
                                          p: 2,
                                          bgcolor: 'grey.50',
                                          borderRadius: 1,
                                          borderLeft: 3,
                                          borderColor: 'primary.main',
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                          <Typography variant="body2" fontWeight="bold" color="primary">
                                            {evaluator.evaluator_name}
                                          </Typography>
                                          <Chip
                                            label={`重み: ${(evaluator.evaluation_weight * 100).toFixed(0)}%`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                          />
                                        </Box>
                                        <Typography variant="body1" sx={{ mb: 0.5 }}>
                                          評価点: {evalScore !== null ? `${evalScore}点` : '－'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {evalComment || '（コメントなし）'}
                                        </Typography>
                                      </Box>
                                    );
                                  })
                                ) : (
                                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="body1">評価点: {item.second_evaluation_score || '－'}点</Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                      {item.second_evaluation_comment || '（コメントなし）'}
                                    </Typography>
                                  </Box>
                                )}
                              </Grid>

                              <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                  <Typography variant="subtitle2">部門長評価点（1-10点）</Typography>
                                  <Box display="flex" gap={1} alignItems="center">
                                    {item.second_evaluation_score && (
                                      <Chip
                                        label={`上長評価との差: ${diff}点`}
                                        color={getDifferenceColor(diff)}
                                        size="small"
                                      />
                                    )}
                                    <Chip
                                      label={`${formData[item.item_detail_id]?.third_evaluation_score || 6}点 - ${getShortScoreDescription(formData[item.item_detail_id]?.third_evaluation_score || 6)}`}
                                      color={getScoreColor(formData[item.item_detail_id]?.third_evaluation_score || 6)}
                                      size="small"
                                    />
                                  </Box>
                                </Box>
                                <Slider
                                  value={formData[item.item_detail_id]?.third_evaluation_score || 6}
                                  onChange={(_, value) =>
                                    handleInputChange(item.item_detail_id, 'third_evaluation_score', value as number)
                                  }
                                  min={1}
                                  max={10}
                                  step={1}
                                  marks={[
                                    { value: 1, label: '1' },
                                    { value: 6, label: '6 (基準)' },
                                    { value: 10, label: '10' },
                                  ]}
                                  valueLabelDisplay="auto"
                                />
                                {/* 評価点の詳細説明 */}
                                <Box
                                  sx={{
                                    mt: 1,
                                    p: 2,
                                    bgcolor: getScoreDescription(formData[item.item_detail_id]?.third_evaluation_score || 6).isStandard
                                      ? 'info.50'
                                      : 'grey.50',
                                    borderRadius: 1,
                                    borderLeft: 4,
                                    borderColor: getScoreDescription(formData[item.item_detail_id]?.third_evaluation_score || 6).isStandard
                                      ? 'info.main'
                                      : 'grey.300',
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary" display="block" fontWeight="bold" mb={0.5}>
                                    評価点の意味：
                                  </Typography>
                                  <Typography variant="body2" color="text.primary">
                                    {getScoreDescription(formData[item.item_detail_id]?.third_evaluation_score || 6).description}
                                  </Typography>
                                </Box>
                              </Grid>

                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={4}
                                  label="部門長評価コメント"
                                  value={formData[item.item_detail_id]?.third_evaluation_comment || ''}
                                  onChange={(e) =>
                                    handleInputChange(item.item_detail_id, 'third_evaluation_comment', e.target.value)
                                  }
                                  placeholder="評価のコメントを入力してください&#13;&#10;例：&#13;&#10;・目標達成状況について&#13;&#10;・特に評価できる点&#13;&#10;・今後の期待や改善点"
                                  helperText="自己評価・上長評価を踏まえた部門長としてのコメントを記入してください"
                                />
                              </Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                );
              })}
          </>
        )}

        {/* 評価結果プレビュー */}
        {performanceResult && (
          <>
            <Divider sx={{ my: 3 }} />
            <Card sx={{ bgcolor: '#f5f5f5' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  【評価結果プレビュー】
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  一時保存後に計算された評価結果です。この結果は部門長のみに表示されます。
                </Alert>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      パフォーマンス評価点(P)
                    </Typography>
                    <Typography variant="h4">
                      {performanceResult.performance_score !== null && performanceResult.performance_score !== undefined
                        ? Number(performanceResult.performance_score).toFixed(2)
                        : '計算中'}点
                    </Typography>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      昇給率はHR確認後に確定されます。部門長評価時点では表示されません。
                    </Alert>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </>
        )}

        {/* 差し戻し履歴セクション */}
        {evaluation?.evaluation_id && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                差し戻し履歴
              </Typography>
              <ReturnCommentHistory
                evaluationId={evaluation.evaluation_id}
                showAll={true}
              />
            </CardContent>
          </Card>
        )}

        {/* ボタン */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
          <Button variant="outlined" onClick={() => navigate(`/dept-head-evaluation/${periodId}`)}>
            一覧に戻る
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {canReturnEvaluation() && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<UndoIcon />}
                onClick={() => setReturnDialogOpen(true)}
              >
                差し戻す
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={handleSaveDraft}
              disabled={saving || !canEvaluate}
            >
              {saving ? <CircularProgress size={24} /> : '一時保存'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={saving || !canEvaluate}
            >
              {saving ? <CircularProgress size={24} /> : '提出'}
            </Button>
          </Box>
        </Box>

        {/* Return Dialog */}
        <ReturnDialog
          open={returnDialogOpen}
          onClose={() => setReturnDialogOpen(false)}
          onSubmit={handleReturn}
          title="上長評価を差し戻す"
          targetName={evaluation?.name}
          maxLength={1000}
        />
      </Paper>
    </Container>
  );
};

export default DeptHeadEvaluationFormPage;
